import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  MapIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  XMarkIcon,
  HeartIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { destinationsAPI } from '../services/api';
import { DestinationMap } from '../components/destinations/DestinationMap';
import { ExternalLinksSection } from '../components/destinations/ExternalLinksSection';
import { CustomDestinationModal } from '../components/destinations/CustomDestinationModal';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  CustomDestination,
  loadCustomDestinations,
  deleteCustomDestination as deleteCustomDest,
  toDestinationResult,
  isCustomDestination,
} from '../utils/customDestinations';
import { getPoiGoogleMapsUrl, getCategoryTripAdvisorUrl } from '../utils/externalLinks';

type ExplorerTab = 'map' | 'search' | 'compare';

interface TabConfig {
  id: ExplorerTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

interface DestinationResult {
  id: string;
  city: string;
  country: string;
  country_code: string;
  region: string;
  lat: number;
  lon: number;
  tagline: string;
  budget_level?: string;
}

interface ProfileData {
  id: string;
  city: string;
  country: string;
  region: string;
  tagline: string;
  safety_rating?: string;
  budget_level?: string;
  best_time_to_visit?: string;
  languages?: string[];
  currency?: string;
  power_voltage?: string;
  visa_info?: string;
  internet_info?: string;
  external_links?: {
    tourism_board?: string;
    wikipedia?: string;
    travel_advisory?: string;
  };
  [key: string]: unknown;
}

interface POIItem {
  name: string;
  description?: string;
  rating?: number;
  price_level?: string;
  category?: string;
  lat?: number;
  lon?: number;
}

interface POIData {
  attractions?: POIItem[];
  restaurants?: POIItem[];
  museums?: POIItem[];
  parks?: POIItem[];
  shopping?: POIItem[];
  nightlife?: POIItem[];
  [key: string]: POIItem[] | undefined;
}

interface WeatherMonth {
  month: string;
  high_c: number;
  low_c: number;
  high_f: number;
  low_f: number;
  rainfall_mm: number;
  is_best?: boolean;
}

interface WeatherData {
  monthly: WeatherMonth[];
  best_months?: string[];
}

interface PhotoItem {
  url: string;
  thumb_url?: string;
  photographer?: string;
  photographer_url?: string;
  alt_description?: string;
}

interface CompareResult {
  destinations: Array<{
    id: string;
    city: string;
    country: string;
    daily_budget?: string;
    safety_rating?: string;
    best_time?: string;
    languages?: string[];
    currency?: string;
    climate?: string;
  }>;
  ai_recommendation?: string;
}

const REGIONS = ['Europe', 'Asia', 'Americas', 'Africa', 'Oceania', 'Middle East'];
const BUDGETS = ['Budget', 'Mid-Range', 'Luxury'];
const POI_CATEGORIES = ['attractions', 'restaurants', 'museums', 'parks', 'shopping', 'nightlife'];
const SUMMARY_FOCUS_OPTIONS = ['general', 'culture', 'food', 'adventure', 'family', 'budget'];

export const DestinationExplorer: React.FC = () => {
  const { t } = useTranslation(['destinations', 'common']);
  const isMobile = useIsMobile();

  const TABS: TabConfig[] = [
    { id: 'map', name: t('destinations:tabs.map'), icon: MapIcon, description: t('destinations:tabDescriptions.map') },
    { id: 'search', name: t('destinations:tabs.search'), icon: MagnifyingGlassIcon, description: t('destinations:tabDescriptions.search') },
    { id: 'compare', name: t('destinations:tabs.compare'), icon: ScaleIcon, description: t('destinations:tabDescriptions.compare') },
  ];

  // Tab state
  const [activeTab, setActiveTab] = useState<ExplorerTab>('map');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DestinationResult[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');
  const [allDestinations, setAllDestinations] = useState<DestinationResult[]>([]);

  // Profile panel state
  const [selectedDestination, setSelectedDestination] = useState<DestinationResult | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // POI state
  const [pois, setPois] = useState<POIData | null>(null);
  const [poiCategory, setPoiCategory] = useState('attractions');
  const [poisLoading, setPoisLoading] = useState(false);
  const [hiddenGems, setHiddenGems] = useState<POIItem[]>([]);
  const [hiddenGemsLoading, setHiddenGemsLoading] = useState(false);

  // AI Summary state
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFocus, setSummaryFocus] = useState('general');

  // Photos state
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  // Weather state
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Compare state
  const [compareList, setCompareList] = useState<string[]>([]);
  const [compareResults, setCompareResults] = useState<CompareResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');
  const [compareSearchResults, setCompareSearchResults] = useState<DestinationResult[]>([]);
  const [aiRecommendation, setAiRecommendation] = useState<string | null>(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);

  // Bookmarks (localStorage)
  const [savedDestinations, setSavedDestinations] = useState<string[]>([]);

  // Custom destinations (localStorage)
  const [customDestinations, setCustomDestinations] = useState<CustomDestination[]>([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [editingCustomDest, setEditingCustomDest] = useState<CustomDestination | null>(null);

  // Loading
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    const loadDestinations = async () => {
      try {
        const data = await destinationsAPI.search({ q: '', limit: 100 });
        setAllDestinations(data.results || []);
      } catch (err) {
        console.error('Failed to load destinations:', err);
      }
    };
    loadDestinations();

    const saved = localStorage.getItem('auxilio-travel-saved-destinations');
    if (saved) {
      try {
        setSavedDestinations(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved destinations:', e);
      }
    }

    // Load custom destinations
    setCustomDestinations(loadCustomDestinations());
  }, []);

  // Merge custom destinations with API destinations for map display
  const mergedDestinations = useMemo(() => {
    const customAsResults = customDestinations.map(toDestinationResult);
    return [...allDestinations, ...customAsResults];
  }, [allDestinations, customDestinations]);

  // Debounced search
  useEffect(() => {
    if (activeTab !== 'search') return;

    const timer = setTimeout(async () => {
      try {
        const params: { q: string; limit: number; region?: string; budget?: string } = {
          q: searchQuery,
          limit: 50,
        };
        if (selectedRegion) params.region = selectedRegion;
        if (selectedBudget) params.budget = selectedBudget;
        const data = await destinationsAPI.search(params);
        const apiResults: DestinationResult[] = data.results || [];

        // Merge matching custom destinations into search results
        const query = searchQuery.toLowerCase();
        const matchingCustom = customDestinations
          .filter((c) => {
            const matchQuery = !query || c.city.toLowerCase().includes(query) || c.country.toLowerCase().includes(query);
            const matchRegion = !selectedRegion || c.region === selectedRegion;
            const matchBudget = !selectedBudget || (c.budget_level || '').toLowerCase() === selectedBudget.toLowerCase();
            return matchQuery && matchRegion && matchBudget;
          })
          .map(toDestinationResult);

        setSearchResults([...matchingCustom, ...apiResults]);
      } catch (err) {
        console.error('Search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedRegion, selectedBudget, activeTab]);

  // Compare search (includes custom destinations)
  useEffect(() => {
    if (!compareSearch.trim()) {
      setCompareSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const data = await destinationsAPI.search({ q: compareSearch, limit: 10 });
        const apiResults = (data.results || []).filter((d: DestinationResult) => !compareList.includes(d.id));

        // Also search custom destinations
        const q = compareSearch.toLowerCase();
        const customMatches = customDestinations
          .filter(cd => !compareList.includes(cd.id))
          .filter(cd =>
            cd.city.toLowerCase().includes(q) ||
            cd.country.toLowerCase().includes(q) ||
            cd.region.toLowerCase().includes(q)
          )
          .map(toDestinationResult);

        // Merge, avoiding duplicates
        const seenIds = new Set(apiResults.map((d: DestinationResult) => d.id));
        const merged = [...apiResults, ...customMatches.filter(d => !seenIds.has(d.id))];
        setCompareSearchResults(merged);
      } catch (err) {
        console.error('Compare search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [compareSearch, compareList, customDestinations]);

  const handleSelectDestination = useCallback(async (dest: DestinationResult) => {
    setShowProfile(true);
    setProfileLoading(true);
    setAiSummary(null);
    setPhotos([]);
    setHiddenGems([]);
    setWeather(null);
    setPois(null);
    setSelectedDestination(dest);
    setError(null);

    // Custom destinations: build profile from localStorage
    if (isCustomDestination(dest.id)) {
      const customDest = customDestinations.find((c) => c.id === dest.id);
      if (customDest) {
        const enrichment = customDest.enrichment;

        if (enrichment && enrichment.enrichment_status) {
          // Enriched custom destination — build full profile
          const costs = enrichment.average_costs || {};
          const bestTime = enrichment.best_time_to_visit;
          const bestTimeStr = bestTime
            ? `${(bestTime.months || []).join(', ')} - ${bestTime.reason || ''}`
            : '';
          const powerInfo = enrichment.power_info || {};

          setProfileData({
            id: customDest.id,
            city: customDest.city,
            country: customDest.country,
            region: customDest.region,
            tagline: customDest.tagline || enrichment.tagline || '',
            safety_rating: enrichment.safety_rating,
            budget_level: costs.budget
              ? `$${costs.budget} / $${costs.midrange || '?'} / $${costs.luxury || '?'} per day`
              : customDest.budget_level
                ? customDest.budget_level.charAt(0).toUpperCase() + customDest.budget_level.slice(1)
                : undefined,
            best_time_to_visit: bestTimeStr,
            languages: enrichment.languages || [],
            currency: enrichment.currency || undefined,
            power_voltage: powerInfo.voltage ? `${powerInfo.voltage}, ${powerInfo.frequency || ''}, Plug: ${powerInfo.plug_type || ''}` : undefined,
            visa_info: enrichment.visa_info || undefined,
            internet_info: enrichment.internet_info || undefined,
            external_links: {
              tourism_board: enrichment.tourism_board_url || undefined,
              wikipedia: enrichment.wikipedia_url || undefined,
              travel_advisory: enrichment.travel_advisory_url || undefined,
            },
            is_custom: true,
            is_enriched: true,
          });

          // Set POIs from enrichment
          const enrichPois = enrichment.pois || {};
          if (Object.keys(enrichPois).length > 0) {
            setPois(enrichPois as any);
          }

          // Set weather from enrichment
          const enrichWeather = enrichment.weather || [];
          if (enrichWeather.length > 0) {
            const monthly = enrichWeather.map((w) => ({
              month: w.month,
              high_c: w.avg_high_c,
              low_c: w.avg_low_c,
              high_f: Math.round(w.avg_high_c * 9 / 5 + 32),
              low_f: Math.round(w.avg_low_c * 9 / 5 + 32),
              rainfall_mm: w.rainfall_mm,
            }));
            const bestMonths = bestTime?.months || [];
            setWeather({ monthly, best_months: bestMonths });
          }

          // Load photos for enriched custom destinations
          try {
            const photosData = await destinationsAPI.getPhotosByQuery(customDest.city, customDest.country);
            const photosList = photosData.photos || [];
            if (photosList.length > 0) {
              setPhotos(photosList.map((p: any) => ({
                url: p.url_regular || p.url_small || p.url,
                thumb_url: p.url_small || p.url_regular,
                photographer: p.photographer,
                photographer_url: p.photographer_url,
                alt_description: p.alt_description,
              })));
            }
          } catch (err) {
            console.error('Failed to load photos for custom destination:', err);
          }
        } else {
          // Non-enriched custom destination — sparse profile
          setProfileData({
            id: customDest.id,
            city: customDest.city,
            country: customDest.country,
            region: customDest.region,
            tagline: customDest.tagline,
            budget_level: customDest.budget_level
              ? customDest.budget_level.charAt(0).toUpperCase() + customDest.budget_level.slice(1)
              : undefined,
            is_custom: true,
          });
        }
      }
      setProfileLoading(false);
      return;
    }

    try {
      const [profile, poisData, weatherData, photosData] = await Promise.all([
        destinationsAPI.getProfile(dest.id),
        destinationsAPI.getPOIs(dest.id),
        destinationsAPI.getWeather(dest.id),
        destinationsAPI.getPhotos(dest.id).catch(() => ({ photos: [] })),
      ]);

      // Transform profile data to expected shape
      const costs = profile.average_costs || {};
      const bestTime = profile.best_time_to_visit;
      const bestTimeStr = typeof bestTime === 'object' && bestTime
        ? `${(bestTime.months || []).join(', ')} - ${bestTime.reason || ''}`
        : String(bestTime || '');
      const countryInfo = profile.country_info || {};
      const languages = countryInfo.languages
        ? Object.values(countryInfo.languages as Record<string, string>)
        : [];
      const currencies = countryInfo.currencies || {};
      const currencyStr = Object.values(currencies)
        .map((c: any) => `${c.name} (${c.symbol || ''})`)
        .join(', ');
      const powerInfo = profile.power_info || {};

      setProfileData({
        ...profile,
        budget_level: `$${costs.budget || '?'} / $${costs.midrange || '?'} / $${costs.luxury || '?'} per day`,
        best_time_to_visit: bestTimeStr,
        languages,
        currency: currencyStr,
        power_voltage: powerInfo.voltage ? `${powerInfo.voltage}, ${powerInfo.frequency}, Plug: ${powerInfo.plug_type}` : undefined,
        external_links: {
          tourism_board: profile.tourism_board_url,
          wikipedia: profile.wikipedia_url,
          travel_advisory: profile.travel_advisory_url,
        },
      });

      // Transform POIs: backend returns flat list, group by category
      const poisArray = poisData.pois || [];
      const grouped: POIData = {};
      for (const poi of poisArray) {
        const cat = poi.category || 'attractions';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat]!.push(poi);
      }
      setPois(grouped);

      // Transform weather data
      const monthlyWeather = (weatherData.monthly_weather || weatherData.monthly || []).map((m: any) => ({
        month: m.month,
        high_c: m.temp_high_c ?? m.high_c ?? 0,
        low_c: m.temp_low_c ?? m.low_c ?? 0,
        high_f: m.temp_high_f ?? m.high_f ?? 0,
        low_f: m.temp_low_f ?? m.low_f ?? 0,
        rainfall_mm: m.precipitation_mm ?? m.rainfall_mm ?? 0,
      }));
      const bestMonthsStr = weatherData.best_months || '';
      const bestMonthsList = typeof bestMonthsStr === 'string'
        ? bestMonthsStr.split(' - ')[0].split(', ').map((s: string) => s.trim()).filter(Boolean)
        : [];
      setWeather({ monthly: monthlyWeather, best_months: bestMonthsList });

      // Auto-load photos
      const photosList = photosData.photos || [];
      if (photosList.length > 0) {
        setPhotos(photosList.map((p: any) => ({
          url: p.url_regular || p.url_small || p.url,
          thumb_url: p.url_small || p.url_regular,
          photographer: p.photographer,
          photographer_url: p.photographer_url,
          alt_description: p.alt_description,
        })));
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load destination profile. Please try again.');
    }
    setProfileLoading(false);
  }, [customDestinations]);

  const toggleSave = useCallback((id: string) => {
    setSavedDestinations(prev => {
      const updated = prev.includes(id)
        ? prev.filter(s => s !== id)
        : [...prev, id];
      localStorage.setItem('auxilio-travel-saved-destinations', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleSaveCustomDestination = useCallback((dest: CustomDestination) => {
    setCustomDestinations(loadCustomDestinations());
  }, []);

  const handleDeleteCustomDestination = useCallback((id: string) => {
    if (!window.confirm(t('destinations:customDestinations.deleteConfirm'))) return;
    deleteCustomDest(id);
    setCustomDestinations(loadCustomDestinations());
    setShowProfile(false);
  }, [t]);

  const handleEditCustomDestination = useCallback(() => {
    if (!selectedDestination || !isCustomDestination(selectedDestination.id)) return;
    const custom = customDestinations.find((c) => c.id === selectedDestination.id);
    if (custom) {
      setEditingCustomDest(custom);
      setShowCustomModal(true);
    }
  }, [selectedDestination, customDestinations]);

  const handleGenerateSummary = async () => {
    if (!selectedDestination) return;
    setSummaryLoading(true);
    try {
      const summaryReq: any = {
        destination_id: selectedDestination.id,
        focus: summaryFocus,
      };
      // For custom destinations, pass city/country so backend can generate without curated data
      if (isCustomDestination(selectedDestination.id)) {
        summaryReq.city = selectedDestination.city;
        summaryReq.country = selectedDestination.country;
      }
      const data = await destinationsAPI.getAISummary(summaryReq);
      setAiSummary(data.summary || data.answer || data.content || '');
    } catch (err) {
      console.error('Failed to generate summary:', err);
      setAiSummary('Failed to generate summary. Please try again.');
    }
    setSummaryLoading(false);
  };

  const handleLoadPhotos = async () => {
    if (!selectedDestination) return;
    setPhotosLoading(true);
    try {
      // For custom destinations, use photos-by-query endpoint
      const data = isCustomDestination(selectedDestination.id)
        ? await destinationsAPI.getPhotosByQuery(selectedDestination.city, selectedDestination.country)
        : await destinationsAPI.getPhotos(selectedDestination.id);
      // Transform Unsplash photo fields to expected shape
      const transformedPhotos = (data.photos || data.results || []).map((p: any) => ({
        url: p.url_regular || p.url || '',
        thumb_url: p.url_small || p.thumb_url || p.url_regular || p.url || '',
        photographer: p.photographer || '',
        photographer_url: p.photographer_url || '',
        alt_description: p.alt_description || '',
      }));
      setPhotos(transformedPhotos);
    } catch (err: any) {
      console.error('Failed to load photos:', err);
      if (err.response?.status === 400 || err.response?.status === 503) {
        setPhotos([]);
      }
    }
    setPhotosLoading(false);
  };

  const handleLoadPOICategory = async (category: string) => {
    if (!selectedDestination) return;
    setPoiCategory(category);
    // For custom destinations, POIs are already loaded from enrichment — just switch tab
    if (isCustomDestination(selectedDestination.id)) return;
    setPoisLoading(true);
    try {
      const data = await destinationsAPI.getPOIs(selectedDestination.id, { category });
      // Transform flat POI list to grouped format
      const poisArray = data.pois || [];
      const grouped: POIData = {};
      for (const poi of poisArray) {
        const cat = poi.category || category;
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat]!.push(poi);
      }
      setPois(prev => ({ ...prev, ...grouped }));
    } catch (err) {
      console.error('Failed to load POIs:', err);
    }
    setPoisLoading(false);
  };

  const handleDiscoverHiddenGems = async () => {
    if (!selectedDestination) return;
    // For custom destinations, hidden gems are not available via backend API
    if (isCustomDestination(selectedDestination.id)) return;
    setHiddenGemsLoading(true);
    try {
      const data = await destinationsAPI.getPOIs(selectedDestination.id, { include_hidden_gems: true });
      // Filter hidden gems from the flat POI list
      const poisArray = data.pois || [];
      const gems = poisArray.filter((p: any) => p.category === 'hidden_gems');
      setHiddenGems(gems);
    } catch (err) {
      console.error('Failed to discover hidden gems:', err);
    }
    setHiddenGemsLoading(false);
  };

  // Build inline custom destination data for the backend compare API
  const buildCustomDestsPayload = () => {
    const customIds = compareList.filter(id => isCustomDestination(id));
    if (customIds.length === 0) return undefined;
    return customIds.map(id => {
      const cd = customDestinations.find(d => d.id === id);
      return {
        id,
        city: cd?.city || '',
        country: cd?.country || '',
        region: cd?.region || '',
        tagline: cd?.tagline || '',
        budget_level: cd?.budget_level || '',
      };
    });
  };

  const handleCompare = async () => {
    if (compareList.length < 2) return;
    setCompareLoading(true);
    setCompareResults(null);
    setAiRecommendation(null);
    try {
      const data = await destinationsAPI.compare({
        destination_ids: compareList,
        custom_destinations: buildCustomDestsPayload(),
      });
      const transformedDests = (data.destinations || []).map((d: any) => {
        const costs = d.average_costs || {};
        const bestTime = d.best_time_to_visit;
        const bestTimeStr = typeof bestTime === 'object' && bestTime
          ? (bestTime.months || []).join(', ')
          : String(bestTime || '');
        return {
          id: d.id,
          city: d.city,
          country: d.country,
          daily_budget: d.is_custom
            ? (d.budget_level ? d.budget_level.charAt(0).toUpperCase() + d.budget_level.slice(1) : '-')
            : `$${costs.budget || '?'} - $${costs.luxury || '?'}`,
          safety_rating: d.safety_rating || '-',
          best_time: bestTimeStr || '-',
          climate: d.climate_type || '-',
          visa_info: d.visa_info || '-',
        };
      });
      setCompareResults({ destinations: transformedDests, ai_recommendation: data.ai_recommendation });
    } catch (err) {
      console.error('Compare failed:', err);
      setError('Failed to compare destinations. Please try again.');
    }
    setCompareLoading(false);
  };

  const handleGetRecommendation = async () => {
    if (compareList.length < 2) return;
    setRecommendationLoading(true);
    try {
      const data = await destinationsAPI.compare({
        destination_ids: compareList,
        include_ai_recommendation: true,
        custom_destinations: buildCustomDestsPayload(),
      });
      setAiRecommendation(data.ai_recommendation || data.recommendation || '');
    } catch (err) {
      console.error('Recommendation failed:', err);
    }
    setRecommendationLoading(false);
  };

  const addToCompare = (dest: DestinationResult) => {
    if (compareList.length >= 3) return;
    if (!compareList.includes(dest.id)) {
      setCompareList(prev => [...prev, dest.id]);
    }
    setCompareSearch('');
    setCompareSearchResults([]);
  };

  const removeFromCompare = (id: string) => {
    setCompareList(prev => prev.filter(d => d !== id));
    setCompareResults(null);
    setAiRecommendation(null);
  };

  const getDestinationById = (id: string): DestinationResult | undefined => {
    const found = allDestinations.find(d => d.id === id);
    if (found) return found;
    // Check custom destinations
    const custom = customDestinations.find(d => d.id === id);
    return custom ? toDestinationResult(custom) : undefined;
  };

  const renderMarkdown = (content: string) => (
    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-600 dark:text-cyan-400 underline hover:text-cyan-700 dark:hover:text-cyan-300"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="text-gray-900 dark:text-gray-100 font-semibold">{children}</strong>
          ),
          h3: ({ children }) => (
            <h3 className="text-gray-900 dark:text-gray-100 text-base font-semibold mt-4 mb-2">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-gray-900 dark:text-gray-100 text-sm font-semibold mt-3 mb-1">{children}</h4>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    for (let i = 0; i < 5; i++) {
      stars.push(
        <StarIcon
          key={i}
          className={`h-4 w-4 ${i < fullStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
        />
      );
    }
    return <div className="flex">{stars}</div>;
  };

  // --- Map Tab ---
  const renderMapTab = () => (
    <div style={{ height: isMobile ? 'calc(100vh - 280px)' : 'calc(100vh - 370px)' }}>
      <DestinationMap
        destinations={mergedDestinations}
        selectedId={selectedDestination?.id}
        onSelect={handleSelectDestination}
      />
    </div>
  );

  // --- Search Tab ---
  const renderSearchTab = () => (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('destinations:search.placeholder')}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap gap-3">
        <select
          value={selectedRegion}
          onChange={(e) => setSelectedRegion(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">{t('destinations:search.allRegions')}</option>
          {REGIONS.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        <select
          value={selectedBudget}
          onChange={(e) => setSelectedBudget(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">{t('destinations:search.allBudgets')}</option>
          {BUDGETS.map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      {/* Results Count */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('destinations:search.results', { count: searchResults.length })}
      </p>

      {/* Results Grid */}
      {searchResults.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchResults.map(dest => (
            <div
              key={dest.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleSelectDestination(dest)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {dest.city}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {dest.country}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSave(dest.id); }}
                  className="ml-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={savedDestinations.includes(dest.id) ? t('destinations:actions.unsave') : t('destinations:actions.save')}
                >
                  {savedDestinations.includes(dest.id) ? (
                    <HeartSolidIcon className="h-5 w-5 text-red-500" />
                  ) : (
                    <HeartIcon className="h-5 w-5 text-gray-400 hover:text-red-400" />
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 italic line-clamp-2">
                {dest.tagline}
              </p>
              <div className="mt-3 flex items-center gap-2">
                {isCustomDestination(dest.id) && (
                  <span className="inline-block px-2 py-0.5 text-xs font-medium bg-cyan-500 text-white rounded">
                    {t('destinations:customDestinations.badge')}
                  </span>
                )}
                <span className="inline-block px-2 py-0.5 text-xs font-medium bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 rounded">
                  {dest.region}
                </span>
                {dest.budget_level && (
                  <span className="inline-block px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                    {dest.budget_level}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <GlobeAltIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>{t('destinations:search.noResults')}</p>
        </div>
      )}
    </div>
  );

  // --- Compare Tab ---
  const renderCompareTab = () => (
    <div className="space-y-6">
      {/* Instruction */}
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {t('destinations:compare.selectDestinations')}
      </p>

      {/* Compare Search */}
      {compareList.length < 3 && (
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={compareSearch}
            onChange={(e) => setCompareSearch(e.target.value)}
            placeholder={t('destinations:compare.searchToAdd')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          {/* Search results dropdown */}
          {compareSearchResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {compareSearchResults.map(dest => (
                <button
                  key={dest.id}
                  onClick={() => addToCompare(dest)}
                  className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
                >
                  <span className="text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    {dest.city}, {dest.country}
                    {isCustomDestination(dest.id) && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 rounded">
                        {t('destinations:customDestinations.badge')}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-cyan-600 dark:text-cyan-400 font-medium">
                    + {t('destinations:compare.addDestination')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {compareList.length >= 3 && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          {t('destinations:compare.maxReached')}
        </p>
      )}

      {/* Selected chips */}
      {compareList.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {compareList.map(id => {
            const dest = getDestinationById(id);
            return (
              <div
                key={id}
                className="flex items-center gap-2 px-3 py-1.5 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 rounded-full text-sm"
              >
                <span>{dest ? `${dest.city}, ${dest.country}` : id}</span>
                <button
                  onClick={() => removeFromCompare(id)}
                  className="hover:text-red-600 dark:hover:text-red-400"
                  title={t('destinations:compare.removeDestination')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Compare Button */}
      <div className="flex justify-center">
        <button
          onClick={handleCompare}
          disabled={compareList.length < 2 || compareLoading}
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {compareLoading ? 'Comparing...' : t('destinations:tabs.compare')}
        </button>
      </div>

      {/* Compare Results Table */}
      {compareResults && compareResults.destinations && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                  Attribute
                </th>
                {compareResults.destinations.map(d => (
                  <th key={d.id} className="px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700">
                    {d.city}, {d.country}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Daily Budget', key: 'daily_budget' },
                { label: 'Safety Rating', key: 'safety_rating' },
                { label: 'Best Time to Visit', key: 'best_time' },
                { label: 'Climate', key: 'climate' },
                { label: 'Visa Info', key: 'visa_info' },
              ].map(row => (
                <tr key={row.key} className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                  <td className="px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300">
                    {row.label}
                  </td>
                  {compareResults.destinations.map(d => {
                    const val = (d as Record<string, unknown>)[row.key];
                    return (
                      <td key={d.id} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {Array.isArray(val)
                          ? (val as string[]).join(', ')
                          : (val as string) || '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* AI Recommendation Button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={handleGetRecommendation}
              disabled={recommendationLoading}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
            >
              {recommendationLoading ? 'Analyzing...' : t('destinations:compare.getRecommendation')}
            </button>
          </div>

          {/* AI Recommendation */}
          {aiRecommendation && (
            <div className="mt-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-cyan-800 dark:text-cyan-200 mb-2">
                {t('destinations:compare.aiRecommendation')}
              </h3>
              {renderMarkdown(aiRecommendation)}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // --- Profile Panel ---
  const renderProfilePanel = () => (
    <div
      className={`fixed inset-y-0 right-0 w-full md:w-[480px] lg:w-[560px] bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 z-50 ${
        showProfile ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="h-full overflow-y-auto">
        {/* Close button */}
        <button
          onClick={() => setShowProfile(false)}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow text-gray-600 dark:text-gray-300 transition-colors"
          title={t('destinations:actions.closeProfile')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {profileLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-cyan-500 animate-spin mx-auto mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading destination...</p>
            </div>
          </div>
        ) : selectedDestination && profileData ? (
          <div>
            {/* Header Banner */}
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-700 dark:from-cyan-600 dark:to-cyan-900 p-6 pt-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">
                  {selectedDestination.city.charAt(0)}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white">
                    {selectedDestination.city}
                  </h2>
                  <p className="text-cyan-100">
                    {selectedDestination.country}
                  </p>
                  <p className="text-sm text-cyan-200 italic mt-1">
                    {selectedDestination.tagline}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {/* Edit/Delete for custom destinations */}
                  {isCustomDestination(selectedDestination.id) && (
                    <>
                      <button
                        onClick={handleEditCustomDestination}
                        className="p-2 rounded-full hover:bg-white/20 transition-colors"
                        title={t('destinations:customDestinations.editTitle')}
                      >
                        <PencilSquareIcon className="h-5 w-5 text-white" />
                      </button>
                      <button
                        onClick={() => handleDeleteCustomDestination(selectedDestination.id)}
                        className="p-2 rounded-full hover:bg-white/20 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5 text-white" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => toggleSave(selectedDestination.id)}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors"
                    title={savedDestinations.includes(selectedDestination.id) ? t('destinations:actions.unsave') : t('destinations:actions.save')}
                  >
                    {savedDestinations.includes(selectedDestination.id) ? (
                      <HeartSolidIcon className="h-6 w-6 text-red-400" />
                    ) : (
                      <HeartIcon className="h-6 w-6 text-white" />
                    )}
                  </button>
                </div>
              </div>

              {/* Custom destination badge */}
              {isCustomDestination(selectedDestination.id) && (
                <div className="mt-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                    {t('destinations:customDestinations.badge')}
                  </span>
                </div>
              )}
            </div>

            <div className="p-5 space-y-6">
              {/* Quick Facts Grid */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                  {t('destinations:profile.quickFacts')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {profileData.safety_rating && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.safety')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.safety_rating}</p>
                    </div>
                  )}
                  {profileData.budget_level && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.averageCosts')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.budget_level}</p>
                    </div>
                  )}
                  {profileData.best_time_to_visit && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.bestTimeToVisit')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.best_time_to_visit}</p>
                    </div>
                  )}
                  {profileData.languages && profileData.languages.length > 0 && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.language')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.languages.join(', ')}</p>
                    </div>
                  )}
                  {profileData.currency && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.currency')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.currency}</p>
                    </div>
                  )}
                  {profileData.power_voltage && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('destinations:profile.power')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{profileData.power_voltage}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Explore & Book - External Links (prominent, right after Quick Facts) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                <ExternalLinksSection
                  city={selectedDestination.city}
                  country={selectedDestination.country}
                  existingLinks={profileData.external_links}
                />
              </div>

              {/* Custom destination note */}
              {(profileData as any).is_custom && !(profileData as any).is_enriched && (
                <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <p className="text-xs text-cyan-800 dark:text-cyan-200">
                    {t('destinations:customDestinations.customNote')}
                  </p>
                </div>
              )}
              {(profileData as any).is_custom && (profileData as any).is_enriched && (
                <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <p className="text-xs text-cyan-800 dark:text-cyan-200">
                    Custom destination — Data gathered from multiple sources.
                  </p>
                </div>
              )}

              {/* Photos Section (auto-loaded for seeded & enriched custom destinations) */}
              {(!(profileData as any).is_custom || (profileData as any).is_enriched) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                    {t('destinations:profile.photos')}
                  </h3>
                  {photos.length === 0 ? (
                    <button
                      onClick={handleLoadPhotos}
                      disabled={photosLoading}
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                    >
                      {photosLoading ? 'Loading photos...' : 'Load Photos'}
                    </button>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {photos.slice(0, 6).map((photo, idx) => (
                        <a
                          key={idx}
                          href={photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 group cursor-pointer"
                        >
                          <img
                            src={photo.thumb_url || photo.url}
                            alt={photo.alt_description || `Photo ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                          {photo.photographer && (
                            <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1.5 py-0.5">
                              <p className="text-white text-[10px] truncate">
                                {photo.photographer}
                              </p>
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* AI Summary Section */}
              {(!(profileData as any).is_custom || (profileData as any).is_enriched) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                    {t('destinations:aiSummary.title')}
                  </h3>
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('destinations:aiSummary.focusLabel')}:
                    </label>
                    <select
                      value={summaryFocus}
                      onChange={(e) => setSummaryFocus(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    >
                      {SUMMARY_FOCUS_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>
                          {t(`destinations:aiSummary.focusOptions.${opt}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleGenerateSummary}
                    disabled={summaryLoading}
                    className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {summaryLoading ? t('destinations:aiSummary.generating') : t('destinations:actions.generateSummary')}
                  </button>
                  {aiSummary && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      {renderMarkdown(aiSummary)}
                    </div>
                  )}
                </div>
              )}

              {/* Points of Interest */}
              {(!(profileData as any).is_custom || (profileData as any).is_enriched) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                    {t('destinations:profile.pois')}
                  </h3>
                  {/* Category Tabs */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {POI_CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => handleLoadPOICategory(cat)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          poiCategory === cat
                            ? 'bg-cyan-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t(`destinations:pois.${cat}`)}
                      </button>
                    ))}
                  </div>

                  {/* POI List */}
                  {poisLoading ? (
                    <div className="text-center py-4">
                      <div className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-700 border-t-cyan-500 animate-spin mx-auto" />
                    </div>
                  ) : pois && pois[poiCategory] && (pois[poiCategory] as POIItem[]).length > 0 ? (
                    <div className="space-y-2">
                      {(pois[poiCategory] as POIItem[]).map((poi, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{poi.name}</p>
                                {/* Per-POI Google Maps link */}
                                <a
                                  href={getPoiGoogleMapsUrl(poi.name, poi.lat, poi.lon)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-0.5 text-gray-400 hover:text-cyan-500 transition-colors flex-shrink-0"
                                  title={t('destinations:externalLinks.viewOnMap')}
                                >
                                  <MapPinIcon className="h-3.5 w-3.5" />
                                </a>
                              </div>
                              {poi.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{poi.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {poi.rating !== undefined && poi.rating > 0 && renderStars(poi.rating)}
                              {poi.price_level && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">{poi.price_level}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No points of interest found for this category.
                    </p>
                  )}

                  {/* Per-category TripAdvisor link */}
                  {selectedDestination && (
                    <a
                      href={getCategoryTripAdvisorUrl(selectedDestination.city, selectedDestination.country, poiCategory)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 mt-3 py-2 text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
                    >
                      <span>{t('destinations:externalLinks.seeMoreOn')}</span>
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  )}

                  {/* Hidden Gems Button */}
                  <button
                    onClick={handleDiscoverHiddenGems}
                    disabled={hiddenGemsLoading}
                    className="mt-3 w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                  >
                    {hiddenGemsLoading ? 'Discovering...' : t('destinations:actions.discoverHiddenGems')}
                  </button>

                  {/* Hidden Gems List */}
                  {hiddenGems.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
                        {t('destinations:pois.hiddenGems')}
                      </h4>
                      {hiddenGems.map((gem, idx) => (
                        <div key={idx} className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{gem.name}</p>
                          {gem.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{gem.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Weather Section */}
              {(!(profileData as any).is_custom || (profileData as any).is_enriched) && weather && weather.monthly && weather.monthly.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                    {t('destinations:weather.title')}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500 dark:text-gray-400">
                          <th className="px-2 py-1.5 text-left font-medium">Month</th>
                          <th className="px-2 py-1.5 text-center font-medium">{t('destinations:weather.high')}</th>
                          <th className="px-2 py-1.5 text-center font-medium">{t('destinations:weather.low')}</th>
                          <th className="px-2 py-1.5 text-center font-medium">{t('destinations:weather.rainfall')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weather.monthly.map((m, idx) => {
                          const isBest = m.is_best || (weather.best_months && weather.best_months.includes(m.month));
                          return (
                            <tr
                              key={idx}
                              className={`border-t border-gray-100 dark:border-gray-700/50 ${
                                isBest ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''
                              }`}
                            >
                              <td className={`px-2 py-1.5 font-medium ${isBest ? 'text-cyan-700 dark:text-cyan-300' : 'text-gray-700 dark:text-gray-300'}`}>
                                {m.month}
                              </td>
                              <td className="px-2 py-1.5 text-center text-gray-700 dark:text-gray-300">
                                {m.high_c}&deg;C / {m.high_f}&deg;F
                              </td>
                              <td className="px-2 py-1.5 text-center text-gray-700 dark:text-gray-300">
                                {m.low_c}&deg;C / {m.low_f}&deg;F
                              </td>
                              <td className="px-2 py-1.5 text-center text-gray-700 dark:text-gray-300">
                                {m.rainfall_mm} mm
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-5 pb-6">
                <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <p className="text-xs text-cyan-800 dark:text-cyan-200">
                    <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                    {t('destinations:disclaimer')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'map': return renderMapTab();
      case 'search': return renderSearchTab();
      case 'compare': return renderCompareTab();
      default: return null;
    }
  };

  return (
    <div className={`flex flex-col ${isMobile ? '' : 'h-full'}`}>
      {/* Header */}
      <div className="flex-none p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          {t('destinations:title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('destinations:subtitle')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex-none border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="flex min-w-max">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setError(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Description */}
      <div className="flex-none px-4 sm:px-6 py-3 bg-cyan-50 dark:bg-cyan-900/10">
        <p className="text-sm text-cyan-800 dark:text-cyan-200">
          {TABS.find(tab => tab.id === activeTab)?.description}
        </p>
      </div>

      {/* Content Area */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto'} p-4 sm:p-6`}>
        {/* Error Message */}
        {error && (
          <div className="mb-4 flex items-start gap-3 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-cyan-800 dark:text-cyan-200">{error}</p>
          </div>
        )}

        {renderActiveTab()}

        {/* Disclaimer */}
        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500 text-center">
          {t('destinations:disclaimer')}
        </p>
      </div>

      {/* Profile Panel */}
      {renderProfilePanel()}

      {/* Backdrop for profile panel on mobile */}
      {showProfile && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setShowProfile(false)}
        />
      )}

      {/* Floating Add Destination Button */}
      {(activeTab === 'map' || activeTab === 'search') && (
        <button
          onClick={() => {
            setEditingCustomDest(null);
            setShowCustomModal(true);
          }}
          className="fixed bottom-20 md:bottom-6 right-6 z-30 p-4 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
          title={t('destinations:customDestinations.addNew')}
        >
          <PlusIcon className="h-6 w-6" />
        </button>
      )}

      {/* Custom Destination Modal */}
      <CustomDestinationModal
        isOpen={showCustomModal}
        onClose={() => {
          setShowCustomModal(false);
          setEditingCustomDest(null);
        }}
        onSave={handleSaveCustomDestination}
        editingDestination={editingCustomDest}
      />

      <LoadingOverlay
        isVisible={loading || compareLoading}
        message="Loading..."
        subMessage="Processing your request"
      />
    </div>
  );
};

export default DestinationExplorer;
